/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useAuth0 } from '@auth0/auth0-react';

// UI lib components
import { AppShell, Group, Text, Flex, Menu, Stack, Box } from '@mantine/core';

// UI local components
import User from '@ui-kit/atoms/User';
import SearchBar from '@features/Search/SearchBar';
import NotificationsMenu from '@layouts/WorkspaceLayout/NotificationsMenu';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// Store
import useAccount from '@features/Account/store/useAccount';

// Types
import { TopBarProps } from './types';

// Local helpers & utils
import { canAccessWorkspace, parseUserName } from '@app/shared/helpers/auth';
import { setViewAsUser } from '@app/shared/providers/ViewAsProvider/store';

// Style
import * as classes from './styles/index.css';
import { useTranslation } from 'react-i18next';
import i18n from '@services/translation/TranslationsProvider/i18n';
import {
  IconChevronRight,
  IconCoin,
  IconDotsVertical,
  IconLanguage,
  IconLogout,
  IconSettings,
} from '@tabler/icons-react';
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';
import * as menuStylesAPI from './styles/menuStylesAPI.css';
import { Button } from '@mantine/core';
import { spacing } from '@app/shared/themes/base';
import { Link } from 'react-router-dom';

// Scoped modules
const { Header } = AppShell;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function TopBar({ user, showUserFullName, ...props }: TopBarProps) {
  // Context
  const workspace = useWorkspace();
  const { t } = useTranslation(['Common', 'Account']);

  // Auth
  const { logout } = useAuth0();

  // Store
  const { account: me } = useAccount();

  /* ***************************** LOCAL VARIABLES **************************** */

  const { fullName: userFullName = '' } = user ? parseUserName(user) : {};

  /* **************************** RENDERING HELPERS *************************** */

  /**
   * Render the central area of the top bar
   */
  function getCentralSection() {
    return (
      <Flex pl="0.7rem" direction="row" flex={1} miw={0}>
        {me && !canAccessWorkspace(me, workspace) ? <Box /> : <SearchBar />}
      </Flex>
    );
  }

  /* ******************************** RENDERING ******************************* */
  return (
    <Header className={classes.topBar} py={0} px={24} {...props}>
      <Group justify="space-between" wrap="nowrap" h="100%">
        {getCentralSection()}

        <Group h="100%" gap={12} wrap="nowrap">
          {import.meta.env.VITE_APP_ACCOUNT_NOTIFICATIONS === 'true' ? (
            <NotificationsMenu />
          ) : (
            <div />
          )}

          <Menu
            classNames={{ ...menuStylesAPI }}
            shadow="md"
            position="bottom-end"
          >
            <Menu.Target>
              <Button
                className={classes.accountMenuButton}
                variant="white"
                color="blackroomGrey"
                h={50}
                px={4}
                styles={{ label: { gap: spacing.xs } }}
              >
                <User user={user} withoutTooltip />
                {showUserFullName && <Text>{userFullName}</Text>}
                <IconDotsVertical />
              </Button>
            </Menu.Target>

            <Menu.Dropdown p={0} pb={4}>
              <Menu.Label p="sm">
                <Group gap="xs">
                  <User user={user} size="lg" withoutTooltip />

                  <Stack gap={2}>
                    <Text size="md" className={classes.userFullName}>
                      {userFullName}
                    </Text>
                    <Text className={classes.userEmail}>{user.email}</Text>
                  </Stack>
                </Group>
              </Menu.Label>

              <Menu.Divider />

              <Menu
                classNames={{ item: menuStylesAPI.item }}
                trigger="hover"
                position="left-start"
                shadow="md"
                offset={4}
              >
                <Menu.Target>
                  <Menu.Item
                    className={classes.languageMenuTarget}
                    leftSection={<IconLanguage size={ICON_SIZE_MAP.smallavg} />}
                    rightSection={
                      <IconChevronRight size={ICON_SIZE_MAP.small} />
                    }
                  >
                    {t('Common:languages.label')}
                  </Menu.Item>
                </Menu.Target>
                <Menu.Dropdown miw="auto">
                  <Menu.Item
                    leftSection="🇬🇧"
                    onClick={() => i18n.changeLanguage('en')}
                  >
                    {t('Common:languages.en')}
                  </Menu.Item>
                  <Menu.Item
                    leftSection="🇫🇷"
                    onClick={() => i18n.changeLanguage('fr')}
                  >
                    {t('Common:languages.fr')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              {/* {import.meta.env.VITE_APP_UI_DARK_MODE === 'true' && (
            <>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconColorScheme size={ICON_SIZE_MAP.smallavg} />}
                onClick={toggleTheme}
              >
                {t(`Common:theme.${colorScheme === 'dark' ? 'light' : 'dark'}`)}
              </Menu.Item>
            </>
          )} */}

              {import.meta.env.VITE_APP_ACCOUNT_SETTINGS === 'true' && (
                <Menu.Item
                  leftSection={<IconSettings size={ICON_SIZE_MAP.smallavg} />}
                  onClick={function cb() {
                    //
                  }}
                >
                  {t('Account:AccountMenu.settings')}
                </Menu.Item>
              )}

              <Menu.Divider />

              {me?.referral && (
                <Link to={`/workspace/${workspace.id}/referral`}>
                  <Menu.Item
                    leftSection={<IconCoin size={ICON_SIZE_MAP.smallavg} />}
                  >
                    {t('Account:AccountMenu.referral')}
                  </Menu.Item>
                </Link>
              )}

              <a
                onClick={async function cb(e) {
                  setViewAsUser(null);
                  logout({
                    logoutParams: { returnTo: window.location.origin },
                  });
                }}
              >
                <Menu.Item
                  leftSection={<IconLogout size={ICON_SIZE_MAP.smallavg} />}
                >
                  {t('Account:AccountMenu.logoutCta')}
                </Menu.Item>
              </a>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </Header>
  );
}

export default TopBar;
